import useInput from 'lib/hooks/useInput';
import { convertToFileExtension, cutUrl, shuffleArray } from 'lib/library';
import { IConvertFile } from 'lib/types/clinic/fileTypes';
import { useDidUpdateEffect } from 'lib/utils';
import React, { createContext, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import { AppActions, FileActions, ProjectActions } from 'store/actionCreators';
import { useShallowAppSelector } from 'store/hooks';
import { v4 as uuid } from 'uuid';
import _ from 'lodash';
import { useProjectFileStore } from 'store/local/projectFile';
import { useProjectDetailStore } from 'store/local/projectDetail';
import { isClinicUserSelector } from 'store/modules/user';
import { clinicImageTypes, clinicModelTypes } from 'lib/mapper';
import { fetchProductData } from 'api/dashboard';
import CustomText from 'components/common/text/CustomText';
import { T } from 'components/common/text';

// import { axs } from 'api/config/axiosConfig';

export const modelTypes = ['obj', 'ply', 'stl'];
export interface CloudFile {
  // changeName: string;
  cloudDataIdx: number;
  // downloadDirectory: string;
  modifyDate: number;
  extension: string;
  fileGroupId: string;
  originName: string;
  uploadState: number;
  uploadUserType: number;
  ownerGroupIdx: number;
  // isUploader: number;
  isOwner: number;
  // viewDirectory: string;
  resourceFileList: string;
  isChat: number;
  //
  // custom add
  type?: string;
  groupName?: string;
  isDownload?: boolean;
  isView?: boolean;
  viewColor?: string | null;
  isChecked?: boolean;
  isDisplayed?: boolean;
  isLast?: boolean;
  isWireframe: boolean;
  opacity: number;
  mesh: any;
  hash: string;
}

export interface LocalFile {
  file: File;
  id: string;
  index: number;
  isChecked: boolean;
  isView: boolean;
  name: string;
  type: string;
  uploadState: boolean;
  isLast?: number[];
  opacity: number;
  hash: string;
}

export const ProjectContext = createContext<any>(null);

// ProjectUploadContainer 와 CreateProjectContainer, ProjectContainer, 를 Provider로서 state 관리
export function ProjectProvider({ children }: { children: React.ReactNode }) {
  const {
    user,
    accessToken,
    projectFilesData: { fileData },
    projectFilesSuccess,
    projectInfo,
    uploadProjectFile,
    uploadProjectFileSuccess,
    fetchProjectSuccess,
    deleteProjectFileSuccess,
    isClinicUser,
  } = useShallowAppSelector(state => ({
    user: state.user.user,
    accessToken: state.auth.accessToken,
    projectFilesData: state.file.projectFiles.data || {},
    projectFilesSuccess: state.file.projectFiles.success,
    // projectFileList: state.project.projectFiles.data?.fileList,
    projectInfo: state.project.project.data?.projectInfo,
    fetchProjectSuccess: state.project.project.success,
    uploadProjectFile: state.file.uploadProjectFile.data || {},
    uploadProjectFileSuccess: state.file.uploadProjectFile.success,
    deleteProjectFileSuccess: state.file.deleteProjectFile.success,
    isClinicUser: isClinicUserSelector(state),
  }));

  const { localFileDeleteSuccess, setLocalFileDeleteSuccess } = useProjectFileStore();
  const { projectId: clinicProjectId } = useProjectDetailStore();

  const { t } = useTranslation();
  const { pathname, search } = useLocation();
  const isCreatePage = `${cutUrl(pathname)}/${cutUrl(pathname, 1)}` === 'project/create';
  const isCreateEditPage = ['project/create', 'project/edit'].includes(
    `${cutUrl(pathname)}/${cutUrl(pathname, 1)}`,
  );
  const isDetailPage = `${cutUrl(pathname)}/${cutUrl(pathname, 1)}` === 'project/detail';
  const history = useHistory();
  const userGroupIdx = user?.userGroupIdx;
  const company = user?.company;
  const projectCode = projectInfo?.projectCode;
  const projectGroupFileList = fileData?.groupFileList;
  const projectPartnerFileList = fileData?.partnerFileList;
  const projectScanFileList = fileData?.scanFileList;
  const projectFileList = fileData?.fileList;
  const projectId = projectInfo?.projectId;

  const clinicFileData = fileData;

  const reworkProjectCode = useInput('');

  const copyProjectCode = useInput('');
  const copyOptionData = useInput([]);

  // Upload Component localFileList
  const localFileListCt = useInput([]) as {
    value: LocalFile[];
    onChange: (e: any) => void;
    setValue: React.Dispatch<any>;
  };

  const partnerFileListCt = useInput([]) as {
    value: CloudFile[];
    onChange: (e: any) => void;
    setValue: React.Dispatch<any>;
  };
  const cloudFileListCt = useInput([]) as {
    value: CloudFile[];
    onChange: (e: any) => void;
    setValue: React.Dispatch<any>;
  };

  const scanFileListCt = useInput([]) as {
    value: CloudFile[];
    onChange: (e: any) => void;
    setValue: React.Dispatch<any>;
  };

  const checkedClinicFiles = useInput([]) as {
    value: IConvertFile[];
    onChange: (e: any) => void;
    setValue: React.Dispatch<any>;
  };

  const clinicViewModelFiles = useInput([]) as {
    value: IConvertFile[];
    onChange: (e: any) => void;
    setValue: React.Dispatch<any>;
  };
  const clinicViewImageFiles = useInput() as {
    value: IConvertFile | null;
    onChange: (e: any) => void;
    setValue: React.Dispatch<any>;
  };
  const clinicViewHtmlFiles = useInput() as {
    value: IConvertFile | null;
    onChange: (e: any) => void;
    setValue: React.Dispatch<any>;
  };

  const upperFiles = useInput([]) as {
    value: IConvertFile[];
    onChange: (e: any) => void;
    setValue: React.Dispatch<any>;
  };
  const lowerFiles = useInput([]) as {
    value: IConvertFile[];
    onChange: (e: any) => void;
    setValue: React.Dispatch<any>;
  };
  const occlusionFiles = useInput([]) as {
    value: IConvertFile[];
    onChange: (e: any) => void;
    setValue: React.Dispatch<any>;
  };
  const photoFiles = useInput([]) as {
    value: IConvertFile[];
    onChange: (e: any) => void;
    setValue: React.Dispatch<any>;
  };
  const etcFiles = useInput([]) as {
    value: IConvertFile[];
    onChange: (e: any) => void;
    setValue: React.Dispatch<any>;
  };
  const noneFiles = useInput([]) as {
    value: IConvertFile[];
    onChange: (e: any) => void;
    setValue: React.Dispatch<any>;
  };

  const deleteCloudFileList = useInput([]) as {
    value: number[];
    onChange: (e: any) => void;
    setValue: React.Dispatch<any>;
  };
  const deleteLocalFileList = useInput([]) as {
    value: string[];
    onChange: (e: any) => void;
    setValue: React.Dispatch<any>;
  };

  const clinicLoadLocalFilesDataList = useInput([]) as {
    value: IConvertFile[];
    onChange: (e: any) => void;
    setValue: React.Dispatch<any>;
  };

  const clinicSortedAllLocalFileList = useInput([]) as {
    value: IConvertFile[];
    onChange: (e: any) => void;
    setValue: React.Dispatch<any>;
  };

  const upperPly = useInput([]) as {
    value: IConvertFile[];
    onChange: (e: any) => void;
    setValue: React.Dispatch<any>;
  };
  const cloudUpperPly = useInput([]) as {
    value: IConvertFile[];
    onChange: (e: any) => void;
    setValue: React.Dispatch<any>;
  };
  const localUpperPly = useInput([]) as {
    value: IConvertFile[];
    onChange: (e: any) => void;
    setValue: React.Dispatch<any>;
  };
  const localUpperJawPly = useInput([]) as {
    value: IConvertFile[];
    onChange: (e: any) => void;
    setValue: React.Dispatch<any>;
  };
  const localUpperPreopPly = useInput([]) as {
    value: IConvertFile[];
    onChange: (e: any) => void;
    setValue: React.Dispatch<any>;
  };
  const localUpperScanbodyPly = useInput([]) as {
    value: IConvertFile[];
    onChange: (e: any) => void;
    setValue: React.Dispatch<any>;
  };
  const upperStl = useInput([]) as {
    value: IConvertFile[];
    onChange: (e: any) => void;
    setValue: React.Dispatch<any>;
  };
  const cloudUpperStl = useInput([]) as {
    value: IConvertFile[];
    onChange: (e: any) => void;
    setValue: React.Dispatch<any>;
  };
  const localUpperStl = useInput([]) as {
    value: IConvertFile[];
    onChange: (e: any) => void;
    setValue: React.Dispatch<any>;
  };
  const localUpperJawStl = useInput([]) as {
    value: IConvertFile[];
    onChange: (e: any) => void;
    setValue: React.Dispatch<any>;
  };
  const localUpperPreopStl = useInput([]) as {
    value: IConvertFile[];
    onChange: (e: any) => void;
    setValue: React.Dispatch<any>;
  };
  const localUpperScanbodyStl = useInput([]) as {
    value: IConvertFile[];
    onChange: (e: any) => void;
    setValue: React.Dispatch<any>;
  };
  const upperObj = useInput([]) as {
    value: IConvertFile[];
    onChange: (e: any) => void;
    setValue: React.Dispatch<any>;
  };
  const cloudUpperObj = useInput([]) as {
    value: IConvertFile[];
    onChange: (e: any) => void;
    setValue: React.Dispatch<any>;
  };
  const localUpperObj = useInput([]) as {
    value: IConvertFile[];
    onChange: (e: any) => void;
    setValue: React.Dispatch<any>;
  };
  const localUpperJawObj = useInput([]) as {
    value: IConvertFile[];
    onChange: (e: any) => void;
    setValue: React.Dispatch<any>;
  };
  const localUpperPreopObj = useInput([]) as {
    value: IConvertFile[];
    onChange: (e: any) => void;
    setValue: React.Dispatch<any>;
  };
  const localUpperScanbodyObj = useInput([]) as {
    value: IConvertFile[];
    onChange: (e: any) => void;
    setValue: React.Dispatch<any>;
  };
  const lowerPly = useInput([]) as {
    value: IConvertFile[];
    onChange: (e: any) => void;
    setValue: React.Dispatch<any>;
  };
  const cloudLowerPly = useInput([]) as {
    value: IConvertFile[];
    onChange: (e: any) => void;
    setValue: React.Dispatch<any>;
  };
  const localLowerPly = useInput([]) as {
    value: IConvertFile[];
    onChange: (e: any) => void;
    setValue: React.Dispatch<any>;
  };
  const localLowerJawPly = useInput([]) as {
    value: IConvertFile[];
    onChange: (e: any) => void;
    setValue: React.Dispatch<any>;
  };
  const localLowerPreopPly = useInput([]) as {
    value: IConvertFile[];
    onChange: (e: any) => void;
    setValue: React.Dispatch<any>;
  };
  const localLowerScanbodyPly = useInput([]) as {
    value: IConvertFile[];
    onChange: (e: any) => void;
    setValue: React.Dispatch<any>;
  };
  const lowerStl = useInput([]) as {
    value: IConvertFile[];
    onChange: (e: any) => void;
    setValue: React.Dispatch<any>;
  };
  const cloudLowerStl = useInput([]) as {
    value: IConvertFile[];
    onChange: (e: any) => void;
    setValue: React.Dispatch<any>;
  };
  const localLowerStl = useInput([]) as {
    value: IConvertFile[];
    onChange: (e: any) => void;
    setValue: React.Dispatch<any>;
  };
  const localLowerJawStl = useInput([]) as {
    value: IConvertFile[];
    onChange: (e: any) => void;
    setValue: React.Dispatch<any>;
  };
  const localLowerPreopStl = useInput([]) as {
    value: IConvertFile[];
    onChange: (e: any) => void;
    setValue: React.Dispatch<any>;
  };
  const localLowerScanbodyStl = useInput([]) as {
    value: IConvertFile[];
    onChange: (e: any) => void;
    setValue: React.Dispatch<any>;
  };
  const lowerObj = useInput([]) as {
    value: IConvertFile[];
    onChange: (e: any) => void;
    setValue: React.Dispatch<any>;
  };
  const cloudLowerObj = useInput([]) as {
    value: IConvertFile[];
    onChange: (e: any) => void;
    setValue: React.Dispatch<any>;
  };
  const localLowerObj = useInput([]) as {
    value: IConvertFile[];
    onChange: (e: any) => void;
    setValue: React.Dispatch<any>;
  };
  const localLowerJawObj = useInput([]) as {
    value: IConvertFile[];
    onChange: (e: any) => void;
    setValue: React.Dispatch<any>;
  };
  const localLowerPreopObj = useInput([]) as {
    value: IConvertFile[];
    onChange: (e: any) => void;
    setValue: React.Dispatch<any>;
  };
  const localLowerScanbodyObj = useInput([]) as {
    value: IConvertFile[];
    onChange: (e: any) => void;
    setValue: React.Dispatch<any>;
  };
  const occlusionPly = useInput([]) as {
    value: IConvertFile[];
    onChange: (e: any) => void;
    setValue: React.Dispatch<any>;
  };
  const cloudOcclusionPly = useInput([]) as {
    value: IConvertFile[];
    onChange: (e: any) => void;
    setValue: React.Dispatch<any>;
  };
  const localOcclusionPly = useInput([]) as {
    value: IConvertFile[];
    onChange: (e: any) => void;
    setValue: React.Dispatch<any>;
  };
  const occlusionStl = useInput([]) as {
    value: IConvertFile[];
    onChange: (e: any) => void;
    setValue: React.Dispatch<any>;
  };
  const cloudOcclusionStl = useInput([]) as {
    value: IConvertFile[];
    onChange: (e: any) => void;
    setValue: React.Dispatch<any>;
  };
  const localOcclusionStl = useInput([]) as {
    value: IConvertFile[];
    onChange: (e: any) => void;
    setValue: React.Dispatch<any>;
  };
  const occlusionObj = useInput([]) as {
    value: IConvertFile[];
    onChange: (e: any) => void;
    setValue: React.Dispatch<any>;
  };
  const cloudOcclusionObj = useInput([]) as {
    value: IConvertFile[];
    onChange: (e: any) => void;
    setValue: React.Dispatch<any>;
  };
  const localOcclusionObj = useInput([]) as {
    value: IConvertFile[];
    onChange: (e: any) => void;
    setValue: React.Dispatch<any>;
  };
  const etcModelPly = useInput([]) as {
    value: IConvertFile[];
    onChange: (e: any) => void;
    setValue: React.Dispatch<any>;
  };
  const cloudEtcModelPly = useInput([]) as {
    value: IConvertFile[];
    onChange: (e: any) => void;
    setValue: React.Dispatch<any>;
  };
  const localEtcModelPly = useInput([]) as {
    value: IConvertFile[];
    onChange: (e: any) => void;
    setValue: React.Dispatch<any>;
  };
  const etcModelStl = useInput([]) as {
    value: IConvertFile[];
    onChange: (e: any) => void;
    setValue: React.Dispatch<any>;
  };
  const cloudEtcModelStl = useInput([]) as {
    value: IConvertFile[];
    onChange: (e: any) => void;
    setValue: React.Dispatch<any>;
  };
  const localEtcModelStl = useInput([]) as {
    value: IConvertFile[];
    onChange: (e: any) => void;
    setValue: React.Dispatch<any>;
  };
  const etcModelObj = useInput([]) as {
    value: IConvertFile[];
    onChange: (e: any) => void;
    setValue: React.Dispatch<any>;
  };
  const cloudEtcModelObj = useInput([]) as {
    value: IConvertFile[];
    onChange: (e: any) => void;
    setValue: React.Dispatch<any>;
  };
  const localEtcModelObj = useInput([]) as {
    value: IConvertFile[];
    onChange: (e: any) => void;
    setValue: React.Dispatch<any>;
  };

  const clinicLocalFileList = useInput([]) as {
    value: IConvertFile[];
    onChange: (e: any) => void;
    setValue: React.Dispatch<any>;
  };

  const cloudPhotoFiles = useInput([]) as {
    value: IConvertFile[];
    onChange: (e: any) => void;
    setValue: React.Dispatch<any>;
  };
  const localPhotoFiles = useInput([]) as {
    value: IConvertFile[];
    onChange: (e: any) => void;
    setValue: React.Dispatch<any>;
  };
  const cloudEtcFiles = useInput([]) as {
    value: IConvertFile[];
    onChange: (e: any) => void;
    setValue: React.Dispatch<any>;
  };
  const localEtcFiles = useInput([]) as {
    value: IConvertFile[];
    onChange: (e: any) => void;
    setValue: React.Dispatch<any>;
  };

  const scanShowiles = useInput([]) as {
    value: IConvertFile[];
    onChange: (e: any) => void;
    setValue: React.Dispatch<any>;
  };

  const scanUpperShowFiles = useInput([]) as {
    value: IConvertFile[];
    onChange: (e: any) => void;
    setValue: React.Dispatch<any>;
  };

  const scanLowerShowFiles = useInput([]) as {
    value: IConvertFile[];
    onChange: (e: any) => void;
    setValue: React.Dispatch<any>;
  };

  const scanOcclusionShowFiles = useInput([]) as {
    value: IConvertFile[];
    onChange: (e: any) => void;
    setValue: React.Dispatch<any>;
  };

  const clinicViewModelFilesCount = useInput(0) as {
    value: number;
    onChange: (e: any) => void;
    setValue: React.Dispatch<any>;
  };

  const scanFileReady = useInput(false) as {
    value: boolean;
    onChange: (e: any) => void;
    setValue: React.Dispatch<any>;
  };

  const classifyClinicFiles = {
    upperFiles,
    lowerFiles,
    occlusionFiles,
    photoFiles,
    etcFiles,
    noneFiles,
    checkedClinicFiles,
    clinicViewModelFiles,
    clinicViewImageFiles,
    clinicViewHtmlFiles,
    deleteCloudFileList,
    deleteLocalFileList,
    clinicLoadLocalFilesDataList,
    clinicSortedAllLocalFileList,
    clinicLocalFileList,
    scanShowiles,
    scanLowerShowFiles,
    scanUpperShowFiles,
    scanOcclusionShowFiles,
    scanFileReady,
    clinicViewModelFilesCount,
  };

  const clinicCloudFiles = {
    cloudUpperPly,
    cloudUpperStl,
    cloudUpperObj,
    cloudLowerPly,
    cloudLowerStl,
    cloudLowerObj,
    cloudOcclusionPly,
    cloudOcclusionStl,
    cloudOcclusionObj,
    cloudEtcModelPly,
    cloudEtcModelStl,
    cloudEtcModelObj,
    cloudPhotoFiles,
    cloudEtcFiles,
  };

  const clinicLocalFiles = {
    localUpperPly,
    localUpperStl,
    localUpperObj,
    localLowerPly,
    localLowerStl,
    localLowerObj,
    localOcclusionPly,
    localOcclusionStl,
    localOcclusionObj,
    localEtcModelPly,
    localEtcModelStl,
    localEtcModelObj,
    localPhotoFiles,
    localEtcFiles,
  };

  const clinicFileListReset = () => {
    console.log('HDS] clinicFileListReset');
    upperFiles.setValue([]);
    cloudUpperPly.setValue([]);
    cloudUpperStl.setValue([]);
    cloudUpperObj.setValue([]);
    localUpperPly.setValue([]);
    localUpperJawPly.setValue([]);
    localUpperPreopPly.setValue([]);
    localUpperScanbodyPly.setValue([]);
    localUpperStl.setValue([]);
    localUpperJawStl.setValue([]);
    localUpperPreopStl.setValue([]);
    localUpperScanbodyStl.setValue([]);
    localUpperObj.setValue([]);
    localUpperJawObj.setValue([]);
    localUpperPreopObj.setValue([]);
    localUpperScanbodyObj.setValue([]);

    lowerFiles.setValue([]);
    cloudLowerPly.setValue([]);
    cloudLowerStl.setValue([]);
    cloudLowerObj.setValue([]);
    localLowerPly.setValue([]);
    localLowerJawPly.setValue([]);
    localLowerPreopPly.setValue([]);
    localLowerScanbodyPly.setValue([]);
    localLowerStl.setValue([]);
    localLowerJawStl.setValue([]);
    localLowerPreopStl.setValue([]);
    localLowerScanbodyStl.setValue([]);
    localLowerObj.setValue([]);
    localLowerJawObj.setValue([]);
    localLowerPreopObj.setValue([]);
    localLowerScanbodyObj.setValue([]);

    occlusionFiles.setValue([]);
    cloudOcclusionPly.setValue([]);
    cloudOcclusionStl.setValue([]);
    cloudOcclusionObj.setValue([]);
    localOcclusionPly.setValue([]);
    localOcclusionStl.setValue([]);
    localOcclusionObj.setValue([]);

    noneFiles.setValue([]);
    cloudEtcModelPly.setValue([]);
    cloudEtcModelStl.setValue([]);
    cloudEtcModelObj.setValue([]);
    localEtcModelPly.setValue([]);
    localEtcModelStl.setValue([]);
    localEtcModelObj.setValue([]);

    photoFiles.setValue([]);
    cloudPhotoFiles.setValue([]);
    localPhotoFiles.setValue([]);

    etcFiles.setValue([]);
    cloudEtcFiles.setValue([]);
    localEtcFiles.setValue([]);

    // scanShowiles.setValue([]);
    scanLowerShowFiles.setValue([]);
    scanUpperShowFiles.setValue([]);
    scanOcclusionShowFiles.setValue([]);
    scanFileReady.setValue(false);

    checkedClinicFiles.setValue([]);
    clinicViewModelFiles.setValue([]);
    clinicViewImageFiles.setValue(null);
    clinicViewHtmlFiles.setValue(null);
    deleteCloudFileList.setValue([]);
    deleteLocalFileList.setValue([]);
    clinicLoadLocalFilesDataList.setValue([]);
    clinicSortedAllLocalFileList.setValue([]);
    clinicViewModelFilesCount.setValue(0);
  };

  useDidUpdateEffect(() => {
    if (!deleteProjectFileSuccess) return;
    ProjectActions.fetch_project_request(projectCode);
  }, [deleteProjectFileSuccess]);

  useDidUpdateEffect(() => {
    const fileList = [...(projectScanFileList || [])];
    scanFileListCt.setValue(fileList?.length ? fileList : []);
  }, [projectScanFileList]);

  useDidUpdateEffect(() => {
    const filteredScanFile = projectScanFileList?.filter((item: any) => {
      return !!item.isShare;
    });
    const fileList = [...(filteredScanFile || []), ...(projectPartnerFileList || [])];

    partnerFileListCt.setValue(fileList?.length ? fileList : []);
  }, [projectPartnerFileList, projectScanFileList]);

  useDidUpdateEffect(() => {
    const fileList = [...(projectScanFileList || []), ...(projectGroupFileList || [])];
    cloudFileListCt.setValue(fileList?.length ? fileList : []);
  }, [projectGroupFileList, projectScanFileList]);

  useDidUpdateEffect(() => {
    if (!projectFilesSuccess || !clinicFileData || !isClinicUser) return;
    // console.log('HDS] projectFilesSuccess _________ ', clinicFileData);
    findScanFiles(clinicFileData);
    // UPPER
    cloudUpperPly.setValue(
      clinicCloudFilesInitState(
        clinicFileData.upper,
        user.userGroupIdx,
        'UPPER',
        'PLY',
        upperFiles.value,
      ),
    );
    cloudUpperStl.setValue(
      clinicCloudFilesInitState(
        clinicFileData.upper,
        user.userGroupIdx,
        'UPPER',
        'STL',
        upperFiles.value,
      ),
    );
    cloudUpperObj.setValue(
      clinicCloudFilesInitState(
        clinicFileData.upper,
        user.userGroupIdx,
        'UPPER',
        'OBJ',
        upperFiles.value,
      ),
    );

    // LOWER
    cloudLowerPly.setValue(
      clinicCloudFilesInitState(
        clinicFileData.lower,
        user.userGroupIdx,
        'LOWER',
        'PLY',
        lowerFiles.value,
      ),
    );
    cloudLowerStl.setValue(
      clinicCloudFilesInitState(
        clinicFileData.lower,
        user.userGroupIdx,
        'LOWER',
        'STL',
        lowerFiles.value,
      ),
    );
    cloudLowerObj.setValue(
      clinicCloudFilesInitState(
        clinicFileData.lower,
        user.userGroupIdx,
        'LOWER',
        'OBJ',
        lowerFiles.value,
      ),
    );

    // OCCLUSION
    cloudOcclusionPly.setValue(
      clinicCloudFilesInitState(
        clinicFileData.occlusion,
        user.userGroupIdx,
        'OCCLUSION',
        'PLY',
        occlusionFiles.value,
      ),
    );
    cloudOcclusionStl.setValue(
      clinicCloudFilesInitState(
        clinicFileData.occlusion,
        user.userGroupIdx,
        'OCCLUSION',
        'STL',
        occlusionFiles.value,
      ),
    );
    cloudOcclusionObj.setValue(
      clinicCloudFilesInitState(
        clinicFileData.occlusion,
        user.userGroupIdx,
        'OCCLUSION',
        'OBJ',
        occlusionFiles.value,
      ),
    );

    // ETCMODEL
    cloudEtcModelPly.setValue(
      clinicCloudFilesInitState(
        clinicFileData.etcModel,
        user.userGroupIdx,
        'NONE',
        'PLY',
        noneFiles.value,
      ),
    );
    cloudEtcModelStl.setValue(
      clinicCloudFilesInitState(
        clinicFileData.etcModel,
        user.userGroupIdx,
        'NONE',
        'STL',
        noneFiles.value,
      ),
    );
    cloudEtcModelObj.setValue(
      clinicCloudFilesInitState(
        clinicFileData.etcModel,
        user.userGroupIdx,
        'NONE',
        'OBJ',
        noneFiles.value,
      ),
    );

    // PHOTO
    cloudPhotoFiles.setValue(
      clinicCloudFilesInitState(clinicFileData.photo, user.userGroupIdx, 'PHOTO'),
    );

    // ETC
    cloudEtcFiles.setValue(clinicCloudFilesInitState(clinicFileData.etc, user.userGroupIdx, 'ETC'));
  }, [
    projectFilesSuccess,
    clinicFileData,
    user,
    isClinicUser,
    upperFiles.value,
    lowerFiles.value,
    occlusionFiles.value,
    photoFiles.value,
    etcFiles.value,
    noneFiles.value,
  ]);

  useDidUpdateEffect(() => {
    upperFiles.setValue([...upperPly.value, ...upperStl.value, ...upperObj.value]);
    console.log('HDS] UPPER REDAT');
  }, [upperPly.value, upperStl.value, upperObj.value]);
  useDidUpdateEffect(() => {
    lowerFiles.setValue([...lowerPly.value, ...lowerStl.value, ...lowerObj.value]);
    occlusionFiles.setValue([...occlusionPly.value, ...occlusionStl.value, ...occlusionObj.value]);
    noneFiles.setValue([...etcModelPly.value, ...etcModelStl.value, ...etcModelObj.value]);
  }, [
    lowerPly.value,
    lowerStl.value,
    lowerObj.value,
    occlusionPly.value,
    occlusionStl.value,
    occlusionObj.value,
    etcModelPly.value,
    etcModelStl.value,
    etcModelObj.value,
  ]);

  useDidUpdateEffect(() => {
    mergeCloudLocalFiles(cloudUpperPly, localUpperPly, upperPly);
  }, [cloudUpperPly.value, localUpperPly.value]);
  useDidUpdateEffect(() => {
    mergeCloudLocalFiles(cloudUpperStl, localUpperStl, upperStl);
  }, [cloudUpperStl.value, localUpperStl.value]);
  useDidUpdateEffect(() => {
    mergeCloudLocalFiles(cloudUpperObj, localUpperObj, upperObj);
  }, [cloudUpperObj.value, localUpperObj.value]);

  useDidUpdateEffect(() => {
    const mergeLocalUpperPly = [
      ...localUpperJawPly.value,
      ...localUpperPreopPly.value,
      ...localUpperScanbodyPly.value,
    ];
    localUpperPly.setValue(mergeLocalUpperPly);
  }, [localUpperJawPly.value, localUpperPreopPly.value, localUpperScanbodyPly.value]);

  useDidUpdateEffect(() => {
    const mergeLocalUpperStl = [
      ...localUpperJawStl.value,
      ...localUpperPreopStl.value,
      ...localUpperScanbodyStl.value,
    ];
    localUpperStl.setValue(mergeLocalUpperStl);
  }, [localUpperJawStl.value, localUpperPreopStl.value, localUpperScanbodyStl.value]);

  useDidUpdateEffect(() => {
    const mergeLocalUpperObj = [
      ...localUpperJawObj.value,
      ...localUpperPreopObj.value,
      ...localUpperScanbodyObj.value,
    ];
    localUpperObj.setValue(mergeLocalUpperObj);
  }, [localUpperJawObj.value, localUpperPreopObj.value, localUpperScanbodyObj.value]);

  useDidUpdateEffect(() => {
    mergeCloudLocalFiles(cloudLowerPly, localLowerPly, lowerPly);
  }, [cloudLowerPly.value, localLowerPly.value]);
  useDidUpdateEffect(() => {
    mergeCloudLocalFiles(cloudLowerStl, localLowerStl, lowerStl);
  }, [cloudLowerStl.value, localLowerStl.value]);
  useDidUpdateEffect(() => {
    mergeCloudLocalFiles(cloudLowerObj, localLowerObj, lowerObj);
  }, [cloudLowerObj.value, localLowerObj.value]);

  useDidUpdateEffect(() => {
    const mergeLocalLowerPly = [
      ...localLowerJawPly.value,
      ...localLowerPreopPly.value,
      ...localLowerScanbodyPly.value,
    ];
    localLowerPly.setValue(mergeLocalLowerPly);
  }, [localLowerJawPly.value, localLowerPreopPly.value, localLowerScanbodyPly.value]);

  useDidUpdateEffect(() => {
    const mergeLocalLowerStl = [
      ...localLowerJawStl.value,
      ...localLowerPreopStl.value,
      ...localLowerScanbodyStl.value,
    ];
    localLowerStl.setValue(mergeLocalLowerStl);
  }, [localLowerJawStl.value, localLowerPreopStl.value, localLowerScanbodyStl.value]);

  useDidUpdateEffect(() => {
    const mergeLocalLowerObj = [
      ...localLowerJawObj.value,
      ...localLowerPreopObj.value,
      ...localLowerScanbodyObj.value,
    ];
    localLowerObj.setValue(mergeLocalLowerObj);
  }, [localLowerJawObj.value, localLowerPreopObj.value, localLowerScanbodyObj.value]);

  useDidUpdateEffect(() => {
    mergeCloudLocalFiles(cloudOcclusionPly, localOcclusionPly, occlusionPly);
  }, [cloudOcclusionPly.value, localOcclusionPly.value]);
  useDidUpdateEffect(() => {
    mergeCloudLocalFiles(cloudOcclusionStl, localOcclusionStl, occlusionStl);
  }, [cloudOcclusionStl.value, localOcclusionStl.value]);
  useDidUpdateEffect(() => {
    mergeCloudLocalFiles(cloudOcclusionObj, localOcclusionObj, occlusionObj);
  }, [cloudOcclusionObj.value, localOcclusionObj.value]);

  useDidUpdateEffect(() => {
    mergeCloudLocalFiles(cloudEtcModelPly, localEtcModelPly, etcModelPly);
  }, [cloudEtcModelPly.value, localEtcModelPly.value]);
  useDidUpdateEffect(() => {
    mergeCloudLocalFiles(cloudEtcModelStl, localEtcModelStl, etcModelStl);
  }, [cloudEtcModelStl.value, localEtcModelStl.value]);
  useDidUpdateEffect(() => {
    mergeCloudLocalFiles(cloudEtcModelObj, localEtcModelObj, etcModelObj);
  }, [cloudEtcModelObj.value, localEtcModelObj.value]);

  useDidUpdateEffect(() => {
    mergeCloudLocalFiles(cloudPhotoFiles, localPhotoFiles, photoFiles);
  }, [cloudPhotoFiles.value, localPhotoFiles.value]);
  useDidUpdateEffect(() => {
    mergeCloudLocalFiles(cloudEtcFiles, localEtcFiles, etcFiles);
  }, [cloudEtcFiles.value, localEtcFiles.value]);

  const mergeCloudLocalFiles = (
    cloudFiles: {
      value: IConvertFile[];
      onChange: (e: any) => void;
      setValue: React.Dispatch<any>;
    },
    localFiles: {
      value: IConvertFile[];
      onChange: (e: any) => void;
      setValue: React.Dispatch<any>;
    },
    merge: {
      value: IConvertFile[];
      onChange: (e: any) => void;
      setValue: React.Dispatch<any>;
    },
  ) => {
    const mergedArray: IConvertFile[] = [...cloudFiles.value];
    localFiles.value.forEach((localFile: IConvertFile) => {
      const index = mergedArray.findIndex(
        (cloudFile: IConvertFile) =>
          !!cloudFile.isMyFile &&
          !!localFile.isMyFile &&
          cloudFile.originName.normalize('NFC') === localFile.originName.normalize('NFC'),
      );
      if (index === -1) {
        // 중복 아닌 경우
        mergedArray.push({
          ...localFile,
          isOwner: 0,
          isOwnerStorage: false,
          isShare: 0,
          isShareStorage: false,
          isView: localFile.isView,
          isWireframe: localFile.isWireframe,
          opacity: localFile.opacity,
          isChecked: localFile.isChecked,
        });
      } else {
        mergedArray[index] = _.merge({}, mergedArray[index], {
          ...localFile,
          compareKey: mergedArray[index].compareKey,
          isOwner: mergedArray[index].isOwner,
          isOwnerStorage: mergedArray[index].isOwnerStorage,
          isShare: mergedArray[index].isShare,
          isShareStorage: mergedArray[index].isShareStorage,
          isView: mergedArray[index].isView,
          isWireframe: mergedArray[index].isWireframe,
          opacity: mergedArray[index].opacity,
          isChecked: mergedArray[index].isChecked,
        });
      }
    });
    merge.setValue(mergedArray);
  };

  const findScanFiles = (clinicFileData: any) => {
    console.log('HDS] findScanFiles 11111 ________ ', clinicFileData);

    scanLowerShowFiles.setValue([]);
    scanUpperShowFiles.setValue([]);
    scanOcclusionShowFiles.setValue([]);
    const upperIsShow =
      clinicFileData?.upper?.filter((item: any) => {
        return item.isShow === 1 && item.size > 0;
      }) || [];
    scanUpperShowFiles.setValue(upperIsShow);
    const lowerIsShow =
      clinicFileData?.lower?.filter((item: any) => {
        return item.isShow === 1 && item.size > 0;
      }) || [];
    scanLowerShowFiles.setValue(lowerIsShow);
    const occlusionIsShow =
      clinicFileData?.occlusion?.filter((item: any) => {
        return item.isShow === 1 && item.size > 0;
      }) || [];
    scanOcclusionShowFiles.setValue(occlusionIsShow);
    scanFileReady.setValue(true);
    // const showFiles = [...upperIsShow, ...lowerIsShow, ...occlusionIsShow];
    // console.log('HDS] findScanFiles 22222 ________ ', showFiles);
    // scanShowiles.setValue(showFiles);
  };

  const clinicCloudFilesInitState = (
    files: any,
    userGroupIdx: number,
    fileType: string,
    extension?: string,
    compareData?: any,
  ) => {
    // console.log('HDS] clinicCloudFilesInitState ________', compareData);
    let initState: IConvertFile[] =
      files?.map((item: any) => {
        const fileExtension = !!item.extension
          ? item.extension.replace('.', '')?.toUpperCase()
          : t('GLOBAL_UNKNOWN');

        const isSameFile = compareData?.find(
          (compare: any) =>
            compare.originName.normalize('NFC') === item.originName.normalize('NFC'),
        );
        return {
          ...item,
          compareKey: `${item.cloudDataIdx}`,
          fileType: fileType,
          id: item.cloudDataIdx,
          originName: item.originName.normalize('NFC'),
          name: item.originName.normalize('NFC'),
          isOwner: item.isOwner,
          isOwnerStorage: !!item.isOwner,
          isShare: item.isShare,
          isShareStorage: !!item.isShare,
          isLocal: 0,
          isLocalStorage: false,
          isView: !!isSameFile ? isSameFile.isView : !!item.isView,
          isWireframe: !!isSameFile ? isSameFile.isWireframe : !!item.isWireframe,
          opacity: !!isSameFile ? isSameFile.opacity : !!item.opacity ? item.opacity : 100,
          fileExtension: fileExtension,
          isChecked: !!isSameFile ? !!isSameFile.isChecked : false,
          isMyFile: userGroupIdx === item.ownerGroupIdx,
        };
      }) || [];

    if (!extension) {
      return initState;
    } else {
      const extensionFilter: IConvertFile[] = initState.filter((file: IConvertFile) => {
        return file.fileExtension === extension;
      });

      return extensionFilter;
    }
  };

  // 체크된 localFileCt.value
  const localFileToUpload = useMemo(() => {
    return localFileListCt.value?.length
      ? localFileListCt.value.filter((item: LocalFile) => !!item.isChecked)
      : [];
  }, [localFileListCt.value]);

  const { ipcRenderer } = window;
  const [uploadFileList, setUploadFileList] = useState<any>([]);
  const [isShared, setIsShared] = useState(0);
  const [uploadSyncPartnerId, setUploadSyncPartnerId] = useState<number | null>(null);
  const [uploadSubmitData, setUploadSubmitData] = useState<any>({});

  const [uploadFileListData, setUploadFileListData] = useState<any>([]);

  const viewColors = useMemo(() => {
    const array = [
      '#f44336',
      '#e91e63',
      '#9c27b0',
      '#673ab7',
      '#3f51b5',
      '#4caf50',
      '#8bc34a',
      '#cddc39',
      '#ffeb3b',
      '#ffc107',
      '#ff9800',
      '#ff5722',
      '#aa2e25',
      '#a31545',
      '#6d1b7b',
      '#2c387e',
      '#482880',
      '#b28704',
      '#b26a00',
      '#b23c17',
    ];
    return shuffleArray(array);
  }, []);

  // NOTE: 중복 파일 확인시 handleUploadLocalFile 호출 전에 확인용
  const createMatchingUserData = (isShare?: number) => {
    // cloudFile과의 matching cloudDataIdx를 찾기 위해
    // const localFileToUpload = localFileListCt.value.filter((item: LocalFile) => !!item.isChecked);
    console.log('localFileToUpload__', localFileToUpload);

    const localFileListData = localFileToUpload?.map((item: any) => {
      const { name, file } = item;
      const currentCloudFileList = isShare === 2 ? partnerFileListCt.value : cloudFileListCt.value;
      const cloudData = currentCloudFileList?.filter(cloudFile => cloudFile.originName === name);
      console.log('cloudData__', cloudData);
      // const machingData = matchingUploadUserTypeData(cloudData);
      const machingData = cloudData.find(item => item.isOwner);
      console.log('machingData__', machingData);
      return {
        cloudDataIdx: machingData?.cloudDataIdx || null,
        file,
        item,
        uploadUserType: machingData?.uploadUserType || null,
      };
    });

    return localFileListData;
  };

  // NOTE: done, remake, uploadLocalFile 등의 함수 실행전 파일 중복여부 확인시 사용
  // NOTE: uploadPartner, uploadGroup 분리
  const handleCheckExistingUploadUserFile = (callback: () => void, isShare?: number) => {
    const localFileListData = createMatchingUserData(isShare);
    // console.log('HDS] localFileListData__', localFileListData);
    const existingCloudFile = localFileListData?.find((item: any) => item.cloudDataIdx);
    // console.log('HDS] existingCloudFile__', existingCloudFile);

    if (!!existingCloudFile) {
      return AppActions.add_popup({
        isOpen: true,
        content: t('MODAL_FILE_DUPLICATE'),
        isCloseIcon: true,
        // contentCardStyle: {
        //   fontSize: '16px',
        // },
        onClick() {
          callback();
        },
      });
    }

    callback();
  };

  const handleUploadLocalFile = async (isShare: number, syncPartnerId?: number) => {
    const projectCodeValue = projectCode;
    if (!projectCodeValue)
      return AppActions.show_toast({ type: 'error', message: 'Bad Request 400' });

    const localFileListData = createMatchingUserData();
    if (!localFileListData?.length) return;
    if (ipcRenderer) {
      localFileListData.map(async (item: any) => {
        // console.log('item __ ', item);
        // console.log('item name __ ', item.item.name);
        const type = item.item.type;
        const name = item.item.name;
        const localFileLoad = await ipcRenderer.invoke('projectFileLocalLoadAsync', {
          projectId,
          fileName: item.item.name,
        });
        const fileBlob = new Blob([new Uint8Array(localFileLoad?.fileData)], { type });
        const fileData = new File([fileBlob], name, {
          type,
        });

        const result = {
          cloudDataIdx: item.cloudDataIdx,
          file: fileData,
          item: item.item,
          uploadUserType: item.uploadUserType,
        };
        setUploadFileListData((draft: any) => draft.concat(result));
      });
      setUploadSubmitData({
        company,
        projectCode: projectCodeValue,
        isShare,
        syncPartnerId,
      });
    }
  };

  useDidUpdateEffect(async () => {
    if (
      ipcRenderer &&
      uploadFileListData.length > 0 &&
      uploadFileListData.length === localFileToUpload.length
    ) {
      const submitData = {
        ...uploadSubmitData,
        project: uploadFileListData,
        projectId,
        type: 'upload',
      };

      const ableUpload = await checkAbailableStorage(uploadFileListData);
      console.log('HDS] ableUpload ', ableUpload);
      if (!ableUpload) {
        AppActions.add_popup({
          isOpen: true,
          width: 660,
          title: <T>GLOBAL_ANNOUNCEMENT</T>,
          isCloseIconButton: false,
          content: (
            <div style={{ textAlign: 'center' }}>
              <CustomText fontSize={20} fontColor="#303030" marginTop={20}>
                <T>GLOBAL_DISABLED_FILE_UPLOAD</T>
              </CustomText>
            </div>
          ),
          onClick() {
            return;
          },
        });
        setUploadFileListData([]);
        setUploadSubmitData({});
        return;
      }
      console.log('HDS] upload_project_file_request continue');
      FileActions.upload_project_file_request(submitData);
      setUploadFileListData([]);
      setUploadSubmitData({});
      //UPLOAD 시작 시, Checkbox 해제
      localFileListCt.setValue(
        localFileListCt.value.reduce((acc: any[], curr: any) => {
          if (curr.isChecked) {
            curr.uploadState = true;
          }
          curr.isChecked = false;

          return acc.concat(curr);
        }, []),
      );
      // setFileUploadFlag(false);
    }
  }, [uploadSubmitData, uploadFileListData]);

  const checkAbailableStorage = async (uploadFileListData: any[]) => {
    let uploadTotal = 0;
    uploadFileListData.forEach((item: any) => {
      uploadTotal = uploadTotal + item.file.size;
    });
    try {
      const response = await fetchProductData();
      if (response.data.status === 200) {
        const remainSize = response.data.dataInfo.remainSize * 1024 * 1024 * 1024;
        console.log('HDS] uploadTotal', uploadTotal);
        console.log('HDS] remainSize', remainSize);
        if (uploadTotal > remainSize) {
          return false;
        } else {
          return true;
        }
      } else {
        return false;
      }
      // data = responseData;
    } catch (error) {
      return false;
    }
  };

  const handleInitLoadLocalFiles = () => {
    if (!ipcRenderer) return;
    // localFileListCt.setValue([]);
    ipcRenderer.once('projectGetRes', (e: any, data: any) => {
      console.log('projectGetRes data', data);
      if (data?.localFileList.length > 0) {
        let modelNumber = 0;
        const returnValue = data.localFileList?.reduce((acc: any[], curr: any, index: number) => {
          // data.localFileList?.reduce((acc: any[], curr: any, index: number) => {
          console.log('curr__ ', curr);
          const { originName, extension, fileSize } = curr;
          // const type = name.slice(name.lastIndexOf('.') + 1);

          // console.log('projectLocalFilesRes partnerFileListCt.value ___ ', partnerFileListCt.value);
          // const cloudData = cloudFileListCt.value?.filter(
          //   cloudFile => cloudFile?.originName === originName,
          // );
          // console.log('projectLocalFilesRes cloudData', cloudData);
          // const machingData = cloudData.find(item => item.isOwner);
          // console.log('projectLocalFilesRes machingData', machingData);
          const fileFullPath = data?.projectDirectory + '\\' + originName;
          if (modelTypes.includes(extension)) modelNumber = modelNumber + 1;
          const obj = {
            // cloudDataIdx: machingData?.cloudDataIdx || null,
            // uploadUserType: machingData?.uploadUserType || null,
            id: uuid(),
            uploadState: false,
            isChecked: false,
            isView: false,
            name: originName,
            type: extension,
            fileFullPath,
            viewColor:
              modelTypes.includes(extension) && modelNumber <= 20
                ? viewColors[modelNumber - 1]
                : null,
            size: fileSize,
            isLast: false,
            opacity: 100,
          };
          // localFileListCt.setValue((draft: LocalFile[]) => draft.concat(obj));
          return acc.concat(obj);
        }, []);

        console.log('projectGetRes data return ___ ', returnValue);
        if (localFileListCt.value.length > 0) {
          // 중복 체크
          const tempNewParter = localFileListCt.value.filter(
            (localFile: any, idx: number, arr: any[]) => {
              return arr.findIndex((item: any) => item.name === localFile.name) === idx;
            },
          );
          // setNewPartnerList(newPartnerListStack);
          localFileListCt.setValue(tempNewParter);
        } else {
          localFileListCt.setValue((draft: LocalFile[]) => draft.concat(returnValue));
        }
        // localFileListCt.setValue(returnValue);
      }
    });
  };

  const handleListenerLocalFiles = (data: any) => {
    if (!ipcRenderer) return;
    let modelNumber = localFileListCt.value.filter((item: any) => !!item.viewColor).length;

    const { action, originName, extension, fileSize, filePath, hash } = data;

    if (action === 'add') {
      if (modelTypes.includes(extension)) modelNumber = modelNumber + 1;
      const obj = {
        id: uuid(),
        uploadState: false,
        isChecked: false,
        isView: false,
        name: originName,
        type: extension,
        filePath,
        viewColor:
          modelTypes.includes(extension) && modelNumber <= 20 ? viewColors[modelNumber - 1] : null,
        size: fileSize,
        isLast: false,
        opacity: 100,
        hash: hash,
      };

      const isDuplicate = !!localFileListCt?.value.find((item: any) => item.name === obj.name);

      if (!isDuplicate) {
        localFileListCt.setValue((draft: LocalFile[]) => [...draft, obj]);
      }
    } else if (action === 'delete') {
      localFileListCt.setValue(() =>
        localFileListCt?.value.filter((item: any) => item.name !== originName),
      );
    }
  };

  // 작업중
  // 작업중
  // 작업중
  // 작업중

  const handleFileCategorazing = (
    fileGroup: {
      value: IConvertFile[];
      onChange: (e: any) => void;
      setValue: React.Dispatch<any>;
    },
    file: IConvertFile,
    check?: boolean,
  ) => {
    fileGroup.setValue((draft: IConvertFile[]) => {
      const findDuplicate = draft.find((item: IConvertFile) => {
        return item.isMyFile && item.originName === file.originName;
      });
      if (!!findDuplicate) {
        return draft.reduce((acc: IConvertFile[], curr: IConvertFile) => {
          if (curr.isMyFile && curr.originName === file.originName) {
            curr.isLocal = 1;
            curr.isLocalStorage = true;
          }
          return acc.concat(curr);
        }, []);
      } else {
        return [
          ...draft,
          {
            ...file,
            id: uuid(),
            isOwner: 0,
            isOwnerStorage: false,
            isShare: 0,
            isShareStorage: false,
          },
        ];
      }
    });
  };
  const handleSortedClinicLocalFiles = (file: any) => {
    const originName = file.originName.normalize('NFC');
    const projectId = file.projectId.normalize('NFC');
    const extension = file.extension.replace('.', '').toUpperCase();
    const fileFullPath = file?.projectDirectory + '\\' + originName;

    let convertFile: IConvertFile;
    if (clinicModelTypes.includes(extension)) {
      if (originName.includes(projectId)) {
        if (originName.toLowerCase().includes('upper')) {
          const scanOption = originName.toLowerCase().includes(`upperjaw-situ`)
            ? 'PREOP'
            : originName.toLowerCase().includes(`upper-marker`)
            ? 'SCANBODY'
            : null;

          convertFile = {
            ...file,
            compareKey: uuid(),
            scanOption: scanOption,
            originName: originName,
            name: originName,
            isLocal: 1,
            isLocalStorage: true,
            isMyFile: true,
            opacity: 100,
            isChecked: false,
            isWireframe: false,
            isView: false,
            fileExtension: extension,
            fileType: 'UPPER',
            fileFullPath,
          };
          if (extension === 'PLY') {
            if (scanOption === 'PREOP') {
              handleFileCategorazing(localUpperPreopPly, convertFile);
            } else if (scanOption === 'SCANBODY') {
              handleFileCategorazing(localUpperScanbodyPly, convertFile);
            } else {
              handleFileCategorazing(localUpperJawPly, convertFile);
            }
            // handleFileCategorazing(localUpperPly, convertFile);
            // handleFileCategorazing(upperPly, convertFile);
          }
          if (extension === 'STL') {
            if (scanOption === 'PREOP') {
              handleFileCategorazing(localUpperPreopStl, convertFile);
            } else if (scanOption === 'SCANBODY') {
              handleFileCategorazing(localUpperScanbodyStl, convertFile);
            } else {
              handleFileCategorazing(localUpperJawStl, convertFile);
            }
            // handleFileCategorazing(localUpperStl, convertFile);
            // handleFileCategorazing(upperStl, convertFile);
          }
          if (extension === 'OBJ') {
            if (scanOption === 'PREOP') {
              handleFileCategorazing(localUpperPreopObj, convertFile);
            } else if (scanOption === 'SCANBODY') {
              handleFileCategorazing(localUpperScanbodyObj, convertFile);
            } else {
              handleFileCategorazing(localUpperJawObj, convertFile);
            }
            // handleFileCategorazing(localUpperObj, convertFile);
            // handleFileCategorazing(upperObj, convertFile);
          }
        }
        if (originName.toLowerCase().includes('lower')) {
          const scanOption = originName.toLowerCase().includes(`lowerjaw-situ`)
            ? 'PREOP'
            : originName.toLowerCase().includes(`lower-marker`)
            ? 'SCANBODY'
            : null;

          convertFile = {
            ...file,
            scanOption: scanOption,
            originName: originName,
            name: originName,
            isLocal: 1,
            isLocalStorage: true,
            isMyFile: true,
            opacity: 100,
            compareKey: uuid(),
            isChecked: false,
            isWireframe: false,
            isView: false,
            fileExtension: extension,
            fileType: 'LOWER',
            fileFullPath,
          };
          if (extension === 'PLY') {
            if (scanOption === 'PREOP') {
              handleFileCategorazing(localLowerPreopPly, convertFile);
            } else if (scanOption === 'SCANBODY') {
              handleFileCategorazing(localLowerScanbodyPly, convertFile);
            } else {
              handleFileCategorazing(localLowerJawPly, convertFile);
            }
            // handleFileCategorazing(localLowerPly, convertFile);
            // handleFileCategorazing(lowerPly, convertFile);
          }
          if (extension === 'STL') {
            if (scanOption === 'PREOP') {
              handleFileCategorazing(localLowerPreopStl, convertFile);
            } else if (scanOption === 'SCANBODY') {
              handleFileCategorazing(localLowerScanbodyStl, convertFile);
            } else {
              handleFileCategorazing(localLowerJawStl, convertFile);
            }
            // handleFileCategorazing(localLowerStl, convertFile);
            // handleFileCategorazing(lowerStl, convertFile);
          }
          if (extension === 'OBJ') {
            if (scanOption === 'PREOP') {
              handleFileCategorazing(localLowerPreopObj, convertFile);
            } else if (scanOption === 'SCANBODY') {
              handleFileCategorazing(localLowerScanbodyObj, convertFile);
            } else {
              handleFileCategorazing(localLowerJawObj, convertFile);
            }
            // handleFileCategorazing(localLowerObj, convertFile);
            // handleFileCategorazing(lowerObj, convertFile);
          }
        }
        if (originName.toLowerCase().includes('occlusion')) {
          const occluNumber = originName.toLowerCase().match(/occlusion(\d+)/);
          const scanOption = `OCCLU${occluNumber[1]}`;
          convertFile = {
            ...file,
            scanOption: scanOption,
            originName: originName,
            name: originName,
            isLocal: 1,
            isLocalStorage: true,
            isMyFile: true,
            opacity: 100,
            compareKey: uuid(),
            isChecked: false,
            isWireframe: false,
            isView: false,
            fileExtension: extension,
            fileType: 'OCCLUSION',
            fileFullPath,
          };
          if (extension === 'PLY') {
            handleFileCategorazing(localOcclusionPly, convertFile);
            // handleFileCategorazing(occlusionPly, convertFile);
          }
          if (extension === 'STL') {
            handleFileCategorazing(localOcclusionStl, convertFile);
            // handleFileCategorazing(occlusionStl, convertFile);
          }
          if (extension === 'OBJ') {
            handleFileCategorazing(localOcclusionObj, convertFile);
            // handleFileCategorazing(occlusionObj, convertFile);
          }
        }
      } else {
        // const scanOption = null;
        const scanOption =
          originName.toLowerCase().includes(`upperjaw-situ`) ||
          originName.toLowerCase().includes(`lowerjaw-situ`)
            ? 'PREOP'
            : originName.toLowerCase().includes(`upper-marker`) ||
              originName.toLowerCase().includes(`lower-marker`)
            ? 'SCANBODY'
            : null;

        convertFile = {
          ...file,
          scanOption: scanOption,
          originName: originName,
          name: originName,
          isLocal: 1,
          isLocalStorage: true,
          isMyFile: true,
          opacity: 100,
          compareKey: uuid(),
          isChecked: false,
          isWireframe: false,
          isView: false,
          fileExtension: extension,
          fileType: 'NONE',
          fileFullPath,
        };
        if (extension === 'PLY') {
          handleFileCategorazing(localEtcModelPly, convertFile);
          // handleFileCategorazing(etcModelPly, convertFile);
        }
        if (extension === 'STL') {
          handleFileCategorazing(localEtcModelStl, convertFile);
          // handleFileCategorazing(etcModelStl, convertFile);
        }
        if (extension === 'OBJ') {
          handleFileCategorazing(localEtcModelObj, convertFile);
          // handleFileCategorazing(etcModelObj, convertFile);
        }
      }
    } else if (clinicImageTypes.includes(extension)) {
      convertFile = {
        ...file,
        originName: originName,
        name: originName,
        isLocal: 1,
        isLocalStorage: true,
        isMyFile: true,
        opacity: 100,
        compareKey: uuid(),
        isChecked: false,
        isWireframe: false,
        isView: false,
        fileExtension: extension,
        fileType: 'PHOTO',
        fileFullPath,
      };
      handleFileCategorazing(localPhotoFiles, convertFile);
      // handleFileCategorazing(photoFiles, convertFile);
    } else {
      convertFile = {
        ...file,
        originName: originName,
        name: originName,
        isLocal: 1,
        isLocalStorage: true,
        isMyFile: true,
        opacity: 100,
        compareKey: uuid(),
        isChecked: false,
        isWireframe: false,
        isView: false,
        fileExtension: extension,
        fileType: 'ETC',
        fileFullPath,
      };
      handleFileCategorazing(localEtcFiles, convertFile, true);
      // handleFileCategorazing(etcFiles, convertFile, true);
    }

    clinicLocalFileList.setValue((draft: IConvertFile[]) => {
      const isDuplicate = draft.some(
        (item: IConvertFile) => item?.originName === convertFile?.originName,
      );

      if (!!isDuplicate) {
        return [...draft];
      } else {
        return [...draft, convertFile];
      }
    });
  };

  const removeLocalFilesController = (
    fileGroup: {
      value: IConvertFile[];
      onChange: (e: any) => void;
      setValue: React.Dispatch<any>;
    },
    fileName: string,
  ) => {
    fileGroup.setValue((draft: IConvertFile[]) =>
      draft.filter(
        (item: IConvertFile) => item.name.normalize('NFC') !== fileName.normalize('NFC'),
      ),
    );
  };

  const handleListenerClinicLocalFiles = async (data: any) => {
    if (!ipcRenderer) return;
    const { action, extension, fileSize, filePath, hash } = data;
    const originName = data.originName.normalize('NFC');
    if (action === 'add') {
      // console.log('HDS] UPPER 1111____________ ', upperFiles.value);

      handleSortedClinicLocalFiles(data);
    } else if (action === 'delete') {
      setLocalFileDeleteSuccess(true);
      console.log('HDS] 로컬 파일 삭제  ____________ ', data);
      handleRomoveLocalFile(originName);
    }
  };

  const handleRomoveLocalFile = (fileName: string) => {
    // removeLocalFilesController(localUpperPly, fileName);
    removeLocalFilesController(localUpperJawPly, fileName);
    removeLocalFilesController(localUpperPreopPly, fileName);
    removeLocalFilesController(localUpperScanbodyPly, fileName);
    // removeLocalFilesController(localUpperStl, fileName);
    removeLocalFilesController(localUpperJawStl, fileName);
    removeLocalFilesController(localUpperPreopStl, fileName);
    removeLocalFilesController(localUpperScanbodyStl, fileName);
    // removeLocalFilesController(localUpperObj, fileName);
    removeLocalFilesController(localUpperJawObj, fileName);
    removeLocalFilesController(localUpperPreopObj, fileName);
    removeLocalFilesController(localUpperScanbodyObj, fileName);

    // removeLocalFilesController(localLowerPly, fileName);
    removeLocalFilesController(localLowerJawPly, fileName);
    removeLocalFilesController(localLowerPreopPly, fileName);
    removeLocalFilesController(localLowerScanbodyPly, fileName);
    // removeLocalFilesController(localLowerStl, fileName);
    removeLocalFilesController(localLowerJawStl, fileName);
    removeLocalFilesController(localLowerPreopStl, fileName);
    removeLocalFilesController(localLowerScanbodyStl, fileName);
    // removeLocalFilesController(localLowerObj, fileName);
    removeLocalFilesController(localLowerJawObj, fileName);
    removeLocalFilesController(localLowerPreopObj, fileName);
    removeLocalFilesController(localLowerScanbodyObj, fileName);

    removeLocalFilesController(localOcclusionPly, fileName);
    removeLocalFilesController(localOcclusionStl, fileName);
    removeLocalFilesController(localOcclusionObj, fileName);

    removeLocalFilesController(localEtcModelPly, fileName);
    removeLocalFilesController(localEtcModelStl, fileName);
    removeLocalFilesController(localEtcModelObj, fileName);

    removeLocalFilesController(localPhotoFiles, fileName);
    removeLocalFilesController(localEtcFiles, fileName);

    removeLocalFilesController(clinicLocalFileList, fileName);
  };

  const handleLoadClinicLocalFiles = () => {
    //localFile list  초기화 후 로드
  };
  // const handleLoadClinicLocalFiles = () => {
  //   //localFile list  초기화 후 로드
  //   if (!ipcRenderer) return;
  //   ipcRenderer.send('projectLocalFiles', {
  //     clinicProjectId,
  //     projectCode,
  //   });
  //   ipcRenderer.on('projectLocalFilesRes', (e: any, data: any) => {
  //     console.log('HDS] handleLoadClinicLocalFiles _______ ');
  //     if (data?.localFileList.length > 0) {
  //       clinicLocalFileList.setValue([]);
  //       const returnValue = data.localFileList?.reduce((acc: any[], curr: any, index: number) => {
  //         const originName = curr.originName.normalize('NFC');
  //         const { action, extension, fileSize, filePath, hash } = curr;
  //         const fileFullPath = data?.projectDirectory + '\\' + originName;

  //         const obj = {
  //           id: uuid(),
  //           ...convertToFileExtension(curr, clinicProjectId),
  //           originName,
  //           name: originName,
  //           extension,
  //           filePath,
  //           size: fileSize,
  //           isLast: false,
  //           hash,
  //           isLocal: 1,
  //           isLocalStorage: true,
  //           isMyFile: true,
  //           opacity: 100,
  //           compareKey: uuid(),
  //           isChecked: false,
  //           isWireframe: false,
  //           isView: false,
  //           fileFullPath,
  //         };
  //         return acc.concat(obj);
  //       }, []);

  //       clinicLocalFileList.setValue((draft: IConvertFile[]) => draft.concat(returnValue));
  //     }
  //   });
  // };

  const handleLoadLocalFiles = () => {
    //localFile list  초기화 후 로드
    // localFileListCt.setValue([]);
    if (!ipcRenderer) return;
    ipcRenderer.send('projectLocalFiles', {
      projectId,
      projectCode,
    });
    ipcRenderer.on('projectLocalFilesRes', (e: any, data: any) => {
      console.log('HDS] handleLoadLocalFiles');
      if (data?.localFileList.length > 0) {
        localFileListCt.setValue([]);
        let modelNumber = 0;
        const returnValue = data.localFileList?.reduce((acc: any[], curr: any, index: number) => {
          // console.log('curr', curr);
          const { originName, extension, fileSize } = curr;
          // const type = name.slice(name.lastIndexOf('.') + 1);

          // console.log('projectLocalFilesRes partnerFileListCt.value ___ ', partnerFileListCt.value);
          // const cloudData = cloudFileListCt.value?.filter(
          //   cloudFile => cloudFile?.originName === originName,
          // );
          // console.log('projectLocalFilesRes cloudData', cloudData);
          // const machingData = cloudData.find(item => item.isOwner);
          // console.log('projectLocalFilesRes machingData', machingData);
          const fileFullPath = data?.projectDirectory + '\\' + originName;
          if (modelTypes.includes(extension)) modelNumber = modelNumber + 1;
          const obj = {
            // cloudDataIdx: machingData?.cloudDataIdx || null,
            // uploadUserType: machingData?.uploadUserType || null,
            id: uuid(),
            isChecked: false,
            isView: false,
            name: originName,
            type: extension,
            fileFullPath,
            viewColor:
              modelTypes.includes(extension) && modelNumber <= 20
                ? viewColors[modelNumber - 1]
                : null,
            size: fileSize,
            opacity: 100,
          };
          return acc.concat(obj);
        }, []);
        console.log('tempFile ____ ', returnValue);

        localFileListCt.setValue((draft: LocalFile[]) => draft.concat(returnValue));
        // localFileListCt.setValue(data?.localFileList);
      }
    });
  };

  const handleClickOpenWorkspace = (projectId: string) => {
    if (!ipcRenderer) return;
    ipcRenderer.send('openWorkspace', { projectId });
  };

  // project 변경시 마다 초기화 FileListCt 초기화
  useDidUpdateEffect(() => {
    console.log('ALL FileListCt Init');
    // cloudFileListCt.setValue([]);
    console.log('HDS] ????????????????????????????????');
    localFileListCt.setValue([]);
    clinicFileListReset();
  }, [projectCode]);

  useDidUpdateEffect(() => {
    if (uploadProjectFileSuccess) {
      localFileListCt.setValue(
        localFileListCt.value.reduce((acc: any[], curr: any) => {
          if (curr.name === uploadProjectFile) {
            curr.uploadState = false;
          }
          return acc.concat(curr);
        }, []),
      );
    }
  }, [uploadProjectFileSuccess, uploadProjectFile]);

  return (
    <ProjectContext.Provider
      value={{
        user,
        // fromActionCt,
        // projectCodeCt,
        reworkProjectCode,
        localFileListCt,
        cloudFileListCt,
        partnerFileListCt,
        scanFileListCt,
        // clinicFileListCt,
        classifyClinicFiles,
        clinicCloudFiles,
        clinicLocalFiles,
        localFileToUpload,
        copyProjectCode,
        copyOptionData,
        viewColors,
        clinicFileListReset,
        // hasNewProjectUploadFile,
        onCheckExistingUploadUserFile: handleCheckExistingUploadUserFile,
        onUploadLocalFile: handleUploadLocalFile,
        onLoadLocalFiles: handleLoadLocalFiles,
        onLoadClinicLocalFiles: handleLoadClinicLocalFiles,
        onInitLoadLocalFiles: handleInitLoadLocalFiles,
        onListenerLocalFiles: handleListenerLocalFiles,
        onListenerClinicLocalFiles: handleListenerClinicLocalFiles,
        onClickOpenWorkspace: handleClickOpenWorkspace,
        onRomoveLocalFile: handleRomoveLocalFile,
      }}
    >
      {children}
    </ProjectContext.Provider>
  );
}
